/**
=========================================================
* Soft UI Dashboard React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import io from "socket.io-client";
import moment from 'moment';

// The Soft UI Dashboard PRO Material main context
const SoftUI = createContext();

// Setting custom name for the context which is visible on react dev tools
SoftUI.displayName = "SoftUIContext";

// Soft UI Dashboard PRO React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "SELECTED_DRIVER": {
      return { ...state, selectedDriver: action.value };
    }
    case "CLEAR_DIRECTIONS": {
      return {...state, selectedDriver: state.selectedDriver ? { ...state.selectedDriver, directions: null } : null }
    }
    case "DRIVERS": {
      return { ...state, drivers: action.value };
    }
    case "DELIVERIES": {
      return { ...state, deliveries: action.value };
    }
    case "PICKUPS": {
      return { ...state, pickups: action.value };
    }
    case "WAYPOINTS": {
      return { ...state, drivers: state.drivers.map(d => d.email === action.value.driver.email ? { ...d, directions: action.value.directions } : d), selectedDriver: { ...state.selectedDriver, directions: action.value.directions}};
    }
    case "WEBSOCKET": {
      return { ...state, websocket: action.value };
    }
    case "WEBSOCKET_ID": {
      return { ...state, websocket_id: action.value };
    }
    case "ACCESS_TOKEN": {
      return { ...state, accessToken: action.value };
    }
    case "USER": {
      return { ...state, user: action.value };
    }
    case "DRIVER_COORDS_UPDATED": {
      const { driver, coords } = action.value;
      return { ...state, drivers: state.drivers.map(d => d.email === driver ? { ...d, coords } : d) };
    }
    case "DELIVERY_COMPLETE": {
      const { delivery } = action.value;
      return { ...state, deliveries: state.deliveries.map(d => d.taskId === delivery.taskId ? delivery : d) };
    }
    case "PICKUP_COMPLETE": {
      const { pickup } = action.value;
      console.log('completed pickup', pickup);
      return { ...state, pickups: state.pickups.map(p => p.taskId === pickup.taskId ? pickup : p) };
    }
    case "CURRENT_DATE": {
      return { ...state, currentDate: action.value };
    }
    case "DRIVER_UPDATE": {
      let { deliveries, pickups, directions } = action.value;
      return { ...state, deliveries, pickups, directions, selectedDriver: { ...state.selectedDriver, directions } };
    }
    case "DATE_UPDATED": {
      let { deliveries, pickups } = action.value;
      return { ...state, deliveries, pickups, selectedDriver: state.selectedDriver ?  { ...state.selectedDriver, directions: null } : null };
    }
    case "TASKS_UPDATED": {
      let deliveries = state.deliveries.map(d => {
        let actionDelivery = action.value.deliveries.find(ad => ad.orders[0].id === d.orders[0].id);
        return actionDelivery ? { ...d, taskId: actionDelivery.taskId, driver: actionDelivery.driver } : d;
      });

      let pickups = state.pickups.map(p => {
        let actionPickup = action.value.pickups.find(ap => ap.returnAuthId === p.returnAuthId);
        return actionPickup ? { ...p, taskId: actionPickup.taskId, driver: actionPickup.driver } : p;
      });
      return { ...state, deliveries, pickups };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Soft UI Dashboard PRO React context provider
function SoftUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    layout: "dashboard",
    selectedDriver: null,
    drivers: [], 
    deliveries: [], 
    pickups: [],
    accessToken: null,
    user: null,
    websocket: null,
    websocket_id: null,
    currentDate: null
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  return <SoftUI.Provider value={[controller, dispatch]}>{children} </SoftUI.Provider>;
}

// Soft UI Dashboard PRO React custom hook for using context
function useSoftUIController() {
  const context = useContext(SoftUI);

  if (!context) {
    throw new Error("useSoftUIController should be used inside the SoftUIControllerProvider.");
  }

  return context;
}

// Typechecking props for the SoftUIControllerProvider
SoftUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setSelectedDriver = (dispatch, value) => dispatch({ type: "SELECTED_DRIVER", value });
const clearDirections = (dispatch) => dispatch({ type: "CLEAR_DIRECTIONS" });
const setDrivers = (dispatch, value) =>  dispatch({ type: "DRIVERS", value });
const setWaypoints = (dispatch, driver, directions) =>  dispatch({ type: "WAYPOINTS", value: { driver, directions } });
const tasksUpdated = (dispatch, value) =>  dispatch({ type: "TASKS_UPDATED", value });
const driverUpdated = (dispatch, value) =>  dispatch({ type: "DRIVER_UPDATE", value });
const dateUpdated = (dispatch, value) =>  dispatch({ type: "DATE_UPDATED", value });
const setDeliveries = (dispatch, value) => dispatch({ type: "DELIVERIES", value });
const setPickups = (dispatch, value) => dispatch({ type: "PICKUPS", value });
const setAccessToken = (dispatch, value) => dispatch({ type: "ACCESS_TOKEN", value });
const setUser = (dispatch, value) => dispatch({ type: "USER", value });
const setDate = (dispatch, value) => dispatch({ type: "CURRENT_DATE", value });
const setWebsocket = (dispatch, value) => dispatch({ type: "WEBSOCKET", value });
const setWebsocketId = (dispatch, value) => dispatch({ type: "WEBSOCKET_ID", value });
const initWebsocket = (dispatch, value) => {    

  let socket = io(`https://fei-dev.fieldenvironmental.com`, { query: {admin: value }});

  socket.on('connect_error', (error) => {
      console.error('error', error);
  });

  // server events
  socket.on('INIT', (data) => {
      dispatch({ type: "WEBSOCKET_ID", value: data });
  });

  socket.on('COORDS', (data) => {
      let coordsData = JSON.parse(data);
      dispatch({type: 'DRIVER_COORDS_UPDATED', value: coordsData})
  });

  socket.on('DELIVERY', (data) => {
    let deliveryData = JSON.parse(data);
    dispatch({type: 'DELIVERY_COMPLETE', value: deliveryData });
  });

  socket.on('PICKUP', (data) => {
    let pickupData = JSON.parse(data);
    dispatch({type: 'PICKUP_COMPLETE', value: pickupData });
  });

  socket.on("TASKS", (data) => {
    let tasks = JSON.parse(data);
    tasksUpdated(dispatch, tasks);
  });
  
  dispatch({ type: "WEBSOCKET", value: socket });
}

const closeWebsocket = (dispatch, websocket) => {
  websocket.close();  
  dispatch({ type: "WEBSOCKET", value: null });
}

export {
  SoftUIControllerProvider,
  useSoftUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setLayout,
  setSelectedDriver,
  clearDirections,
  setDrivers,
  driverUpdated,
  dateUpdated,
  setDeliveries,
  setPickups,
  setWaypoints,
  setAccessToken,
  setUser,
  setDate,
  setWebsocket,
  setWebsocketId,
  initWebsocket,
  closeWebsocket,
};
